import dayjs from 'dayjs'
import { BC_GL_OBJECT_TYPE_ID } from '@/constants/bcNeedsAttention'
import type {
    BcDocumentField,
    BcDocumentType,
    BcFieldChange,
    BcTable,
    BcTimelineEntry,
    BcTransaction,
} from '@/types/bcNeedsAttention.types'

/**
 * Error thrown by every `/api/BCNeedsAttention` hook. The API answers errors
 * (other than a Resend refusal) with a plain JSON string, and the screens need
 * the status to choose a state: 403 access denied, 404 not found, 503 vendor
 * search unavailable.
 */
export class BcApiError extends Error {
    readonly status: number

    constructor(status: number, body: unknown) {
        super(typeof body === 'string' && body !== '' ? body : `Request failed (${status})`)
        this.name = 'BcApiError'
        this.status = status
    }
}

export function bcErrorStatus(error: unknown): number | undefined {
    return error instanceof BcApiError ? error.status : undefined
}

/**
 * `0`, `""` and `null` all mean "none" in this API — a GL row carries 0 / "" for
 * every payout field.
 */
export function isBcNone(value: unknown): boolean {
    return value === null || value === undefined || value === 0 || value === ''
}

export const BC_NONE = '—'

export function bcDisplay(value: string | number | null | undefined): string {
    return isBcNone(value) ? BC_NONE : String(value)
}

/**
 * Unix seconds → `YYYY-MM-DD HH:mm` in the viewer's time zone — the format both
 * the design and the backend contract specify. One helper, so a later switch to
 * the estate's `formatUnixTimestamp` is a one-line change.
 */
export function formatBcTime(unixSeconds: number | null | undefined): string {
    if (isBcNone(unixSeconds)) return BC_NONE
    return dayjs.unix(Number(unixSeconds)).format('YYYY-MM-DD HH:mm')
}

/** Which table a transaction belongs to — by object type, as the API decides it. */
export function bcTableOf(transaction: Pick<BcTransaction, 'objectTypeId'>): BcTable {
    return transaction.objectTypeId === BC_GL_OBJECT_TYPE_ID ? 'GL' : 'Payouts'
}

export type BcDocumentKind = 'invoice' | 'creditMemo' | 'journal' | 'unknown'

/** The document a row carries. A GL row is a journal line whatever its `bcDocumentType` says. */
export function bcRowDocumentKind(
    row: Pick<BcTransaction, 'objectTypeId' | 'bcDocumentType'>,
): BcDocumentKind {
    return bcTableOf(row) === 'GL' ? 'journal' : bcDocumentKind(row.bcDocumentType)
}

export function bcDocumentKind(type: BcDocumentType): BcDocumentKind {
    switch (type) {
        case 'purchaseInvoices':
            return 'invoice'
        case 'purchaseCreditMemos':
            return 'creditMemo'
        case 'journalLines':
            return 'journal'
        default:
            return 'unknown'
    }
}

/**
 * The row's latest attempt when it was recorded but never reached Business
 * Central (a NotQueued resend), from the History timeline; undefined otherwise.
 * The row stays To review with Business Central's previous refusal.
 */
export function bcNotSentAttempt(
    timeline: BcTimelineEntry[],
    attemptNumber: number,
): BcTimelineEntry | undefined {
    const last = timeline.filter((entry) => entry.kind === 'Attempt').pop()
    return last?.result === 'NotSent' && last.attempt === attemptNumber ? last : undefined
}

/**
 * The last attempt Business Central itself refused. After a NotQueued resend the API rewrites the
 * row's `lastError` / `reason` with the queue failure (code `BOSS.NotQueued`, category Other), so
 * the row no longer says what Business Central refused — the timeline still does.
 */
export function bcLastRefusal(timeline: BcTimelineEntry[]): BcTimelineEntry | undefined {
    return timeline.filter((entry) => entry.kind === 'Attempt' && entry.result === 'Rejected').pop()
}

// ── Server text, translated on the screen (Q24) ────────────────────────

/**
 * One sentence the API sends in English that the screen can show in French: the
 * whole English text as an anchored pattern, with named groups for the values it
 * carries (a document number, an account), and its translation. `inner`
 * translates a nested message — the backend sometimes puts one inside another.
 */
export interface BcServerTextRule {
    pattern: RegExp
    translate: (values: Record<string, string>, inner: (text: string) => string) => string
}

/**
 * The server's text translated by the first rule whose pattern matches it, or the
 * text unchanged when none does — a message the backend adds or rewords shows in
 * English rather than not at all.
 */
export function translateBcServerText(text: string, rules: BcServerTextRule[]): string {
    const trimmed = text.trim()
    for (const rule of rules) {
        const match = rule.pattern.exec(trimmed)
        if (match) {
            return rule.translate(match.groups ?? {}, (inner) =>
                translateBcServerText(inner, rules),
            )
        }
    }
    return text
}

// ── The form, built from `fields[]` ─────────────────────────────────────

export interface BcFieldSections {
    /** `line === null` — the document's own fields. */
    header: BcDocumentField[]
    /** One entry per line, in line order. */
    lines: Array<{ line: number; fields: BcDocumentField[] }>
}

export function groupBcFields(fields: BcDocumentField[]): BcFieldSections {
    const header: BcDocumentField[] = []
    const byLine = new Map<number, BcDocumentField[]>()
    for (const field of fields) {
        if (field.line === null) {
            header.push(field)
            continue
        }
        const list = byLine.get(field.line) ?? []
        list.push(field)
        byLine.set(field.line, list)
    }
    const lines = [...byLine.entries()]
        .sort(([a], [b]) => a - b)
        .map(([line, list]) => ({ line, fields: orderLineFields(list) }))
    return { header, lines }
}

/**
 * The design lists a line's correctable fields first (Account, Amount,
 * Description), then the locked ones. Names it does not know keep the server's
 * order, after the known ones.
 */
const LINE_FIELD_ORDER = [
    'lineObjectNumber',
    'unitCost',
    'description',
    'lineType',
    'quantity',
    'taxCode',
]

function orderLineFields(fields: BcDocumentField[]): BcDocumentField[] {
    const rank = (field: BcDocumentField) => {
        const index = LINE_FIELD_ORDER.indexOf(bcFieldName(field.path))
        return index === -1 ? LINE_FIELD_ORDER.length : index
    }
    return fields
        .map((field, index) => ({ field, index }))
        .sort((a, b) => rank(a.field) - rank(b.field) || a.index - b.index)
        .map(({ field }) => field)
}

/** The server labels line fields "Amount (line 2)"; inside "Line 2 of 3" the suffix repeats itself. */
export function stripBcLineSuffix(label: string): string {
    return label.replace(/\s*\(line \d+\)\s*$/i, '')
}

/** The last segment of a field path: `purchaseInvoiceLines[0].unitCost` → `unitCost`. */
export function bcFieldName(path: string): string {
    const last = path.split('.').pop() ?? path
    return last.replace(/\[\d+\]$/, '')
}

export type BcFieldKind = 'vendor' | 'date' | 'amount' | 'code' | 'description' | 'text'

/** Decides the input a field gets. Paths, not labels, so it holds in French. */
export function bcFieldKind(path: string): BcFieldKind {
    const name = bcFieldName(path)
    if (name === 'vendorNumber') return 'vendor'
    if (/date$/i.test(name)) return 'date'
    if (name === 'unitCost' || name === 'amount') return 'amount'
    if (name === 'lineObjectNumber' || name === 'accountNumber') return 'code'
    if (name === 'description') return 'description'
    return 'text'
}

const NEVER_REFUSED_KINDS = new Set<BcFieldKind>(['amount', 'date', 'description'])

/**
 * The fields Business Central's refusal is about, so its reason can sit under them instead of in
 * a banner (FF-2870): the vendor when the vendor is unknown, else every field still holding the
 * refused value (`reasonValue`: an account, a code). Amounts, dates and descriptions are never
 * matched. Empty when no field explains the refusal (a tax setup, a division with no company):
 * the banner keeps those.
 */
export function bcRejectedFieldPaths(
    tx: Pick<BcTransaction, 'category' | 'reasonValue'>,
    fields: BcDocumentField[],
): string[] {
    if (tx.category === 'UnknownVendor') {
        return fields.filter((f) => bcFieldKind(f.path) === 'vendor').map((f) => f.path)
    }
    const refused = tx.reasonValue?.trim().toUpperCase()
    if (!refused) return []
    return fields
        .filter((f) => !NEVER_REFUSED_KINDS.has(bcFieldKind(f.path)))
        .filter((f) => (f.value ?? '').trim().toUpperCase() === refused)
        .map((f) => f.path)
}

/**
 * Payout documents carry the document date and the posting date as one value:
 * changing either moves both. Returns the path of the other date, or undefined
 * when the field is not one of a linked, editable pair (GL dates are locked).
 */
export function bcLinkedDatePath(path: string, fields: BcDocumentField[]): string | undefined {
    if (bcFieldKind(path) !== 'date') return undefined
    const dates = fields.filter(
        (f) => f.line === null && f.editable && bcFieldKind(f.path) === 'date',
    )
    if (dates.length !== 2 || !dates.some((f) => f.path === path)) return undefined
    return dates.find((f) => f.path !== path)?.path
}

/** Form values keyed by field path. */
export type BcFieldValues = Record<string, string>

export function bcInitialValues(fields: BcDocumentField[]): BcFieldValues {
    return Object.fromEntries(fields.map((f) => [f.path, f.value ?? '']))
}

/** Codes are upper-cased by the server; compare and send them the same way. */
export function normalizeBcValue(path: string, value: string): string {
    const trimmed = value.trim()
    return bcFieldKind(path) === 'code' || bcFieldKind(path) === 'vendor'
        ? trimmed.toUpperCase()
        : trimmed
}

/** Amounts arrive as "2000"; a form may hold "2000.00". Same number, no change. */
function sameBcValue(path: string, a: string, b: string): boolean {
    if (bcFieldKind(path) === 'amount') {
        const x = Number(a)
        const y = Number(b)
        if (Number.isFinite(x) && Number.isFinite(y)) return x === y
    }
    return normalizeBcValue(path, a) === normalizeBcValue(path, b)
}

/** The Resend body: only editable fields whose value changed, in form order. */
export function buildBcChanges(fields: BcDocumentField[], values: BcFieldValues): BcFieldChange[] {
    return fields
        .filter((f) => f.editable && f.path in values)
        .filter((f) => !sameBcValue(f.path, f.value ?? '', values[f.path] ?? ''))
        .map((f) => ({ fieldPath: f.path, newValue: normalizeBcValue(f.path, values[f.path]) }))
}

// ── Form state ↔ wire values ────────────────────────────────────────────
// The inputs hold native types (FormDateInput a Date, FormCurrencyInput a
// number); the API speaks strings: `yyyy-MM-dd`, amounts with a full stop.

export type BcFormValue = string | number | Date | null | undefined

/** The API's string → what the input for this field holds. */
export function toBcFormValue(path: string, value: string): BcFormValue {
    switch (bcFieldKind(path)) {
        case 'date': {
            // By hand rather than dayjs's customParseFormat, which only main.tsx extends.
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
            return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
        }
        case 'amount': {
            const n = Number(value)
            return value === '' || !Number.isFinite(n) ? '' : n
        }
        default:
            return value
    }
}

/** What the input holds → the string the API expects. */
export function toBcWireValue(path: string, value: BcFormValue): string {
    if (value === null || value === undefined) return ''
    switch (bcFieldKind(path)) {
        case 'date':
            return value instanceof Date ? dayjs(value).format('YYYY-MM-DD') : String(value)
        case 'amount': {
            const n = Number(value)
            return value === '' || !Number.isFinite(n) ? '' : String(Math.round(n * 100) / 100)
        }
        default:
            return String(value)
    }
}
